import { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { Tenant, TenantTheme } from '../types';
import { useTenant } from './useTenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../api/tenant.api';
import { IOnChange } from '../views/components';
import { useFetch } from './useFetch';

export const useSettings = (
  screen: 'appearance' | 'basic-information' = 'basic-information'
) => {
  const BANNER_LIMIT = 10;

  const [tenantInfo, setTenantInfo] = useState<Partial<Tenant>>({});
  const [tenantTheme, setTenantTheme] = useState<Partial<TenantTheme>>({});

  // Track initial supporting currencies to filter out existing ones in API calls
  const initialSupportingCurrenciesRef = useRef<string[]>([]);

  const { tenant, loading: loadingTenant, refreshTenant } = useTenant();

  const queryClient = useQueryClient();

  const { useFetchTenantTheme, useFetchCurrencyPricing } = useFetch();
  const { data: theme } = useFetchTenantTheme({
    enabled: screen === 'appearance',
  });

  // The settings screen loads the ADMIN theme (includes imageStylePrompt, which the
  // public theme endpoint intentionally omits). Falls back to the public theme.
  const { data: adminTheme } = useQuery({
    queryKey: ['tenant-theme-admin', tenant?.id],
    queryFn: () => tenantApi.getTenantThemeAdmin(tenant!.id),
    enabled: screen === 'appearance' && !!tenant?.id,
  });

  // Full currency list (id, code, name, symbol) for code -> id mapping
  const { data: currencyPricing } = useFetchCurrencyPricing();

  // Fetch supported currencies
  const { data: supportedCurrencies, isLoading: loadingSupportedCurrencies } =
    useQuery({
      queryKey: ['supported-currencies'],
      queryFn: async () => {
        const response = await tenantApi.getSupportedCurrencies();
        return response.data;
      },
    });

  // Fetch base currency
  const { data: baseCurrency, isLoading: loadingBaseCurrency } = useQuery({
    queryKey: ['base-currency'],
    queryFn: async () => {
      const response = await tenantApi.getBaseCurrency();
      return response.data;
    },
  });

  const { isLoading: updatingTenant, mutate: updateTenant } = useMutation({
    mutationFn: async () => {
      // Only update tenant name
      await tenantApi.updateTenant(tenant?.id!, { name: tenantInfo.name });

      // Then handle supporting currencies if they've been selected (UI holds codes)
      if (tenantInfo.supportingCurrencies?.length) {
        // Filter to only send currency codes that are not already supported
        const newCurrencyCodes = tenantInfo.supportingCurrencies.filter(
          (code) => !initialSupportingCurrenciesRef.current.includes(code)
        );

        if (newCurrencyCodes.length > 0) {
          // Map codes -> IDs from the full pricing list
          const allCurrencies: Array<{ id: string; code: string }> =
            (currencyPricing?.data as Array<{ id: string; code: string }>) ||
            [];
          const codeToId = new Map<string, string>(
            allCurrencies.map((c) => [c.code, c.id])
          );

          const currencyIds = newCurrencyCodes
            .map((code) => codeToId.get(code) ?? null)
            .filter((id): id is string => id !== null);

          // Add only the new supporting currencies if we got any IDs
          if (currencyIds.length > 0) {
            await tenantApi.addSupportingCurrencies({
              currencyIds,
              isBase: false,
              isSupported: true,
            });
          }
        }
      }
    },
    onSuccess: () => {
      refreshTenant();
      queryClient.refetchQueries({ queryKey: ['supported-currencies'] });
    },
  });

  const { mutate: getCurrencyByCode, isLoading: loadingCurrency } = useMutation(
    {
      mutationFn: async (code: string) => {
        const response = await tenantApi.getCurrencyByCode(code);
        return response.data;
      },
    }
  );

  const { mutate: updateCurrency, isLoading: updatingCurrency } = useMutation({
    mutationFn: ({
      currencyId,
      isBase,
      isSupported,
    }: {
      currencyId: string | number;
      isBase?: boolean;
      isSupported?: boolean;
    }) => tenantApi.updateTenantCurrency(currencyId, { isBase, isSupported }),
    onSuccess: () => {
      // Refetch both base currency and supported currencies after changes
      queryClient.invalidateQueries({ queryKey: ['base-currency'] });
    },
  });

  const flattenTenantTheme = (theme: Partial<TenantTheme>) => {
    const flattened: any = {
      isDark: theme.darkMode || false,
    };

    if (theme.styles) {
      const {
        fontFamily,
        fontUrl,
        primaryColor,
        secondaryColor,
        backgroundColor,
        textColor,
        imageStylePrompt,
      } = theme.styles;
      if (primaryColor) flattened.primaryColor = primaryColor;
      if (secondaryColor) flattened.secondaryColor = secondaryColor;
      if (backgroundColor) flattened.backgroundColor = backgroundColor;
      if (textColor) flattened.textColor = textColor;
      if (fontFamily) flattened.fontFamily = fontFamily;
      if (fontUrl) flattened.fontUrl = fontUrl;
      // Allow clearing the prompt: send empty string through too.
      if (imageStylePrompt !== undefined)
        flattened.imageStylePrompt = imageStylePrompt;
    }

    if (theme.assets) {
      const { logoUrl, faviconUrl, banners } = theme.assets;
      if (logoUrl) flattened.logoUrl = logoUrl;
      if (faviconUrl) flattened.faviconUrl = faviconUrl;
      if (banners) flattened.banners = banners;
    }

    if (theme.styles?.cssVariables) {
      flattened.cssVariables = theme.styles.cssVariables;
    }

    return flattened;
  };

  const { isLoading: updatingTenantTheme, mutate: updateTenantTheme } =
    useMutation({
      mutationFn: () => {
        const flattenedTheme = flattenTenantTheme(tenantTheme);
        console.log('Flattened theme payload:', flattenedTheme);
        return tenantApi.updateTenantTheme(tenant?.id!, flattenedTheme);
      },
      onSuccess: () => {
        toast.success('Theme updated successfully!');
        queryClient.invalidateQueries({ queryKey: ['tenant-theme'] });
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to update theme. Please try again.'
        );
      },
    });

  const updateTenantInfo = (
    event: React.SyntheticEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const value = event.currentTarget.value;
    const name = event.currentTarget.name;

    // Only update the name field, preserve other fields as is
    if (name === 'name') {
      setTenantInfo({
        ...tenantInfo,
        name: value,
      });
    }
  };

  const { mutate: removeSupportingCurrency } = useMutation({
    mutationFn: async (currencyId: string) => {
      await tenantApi.removeSupportingCurrency(currencyId);
    },
  });

  const updateSupportingCurrencies: IOnChange<{
    label: string;
    value: string;
  }> = (services, actionMeta) => {
    const selectedCurrencies = services as { label: string; value: string }[];
    const selectedCodes = selectedCurrencies.map((s) => s.value);

    // Keep UI values as codes in state
    setTenantInfo({
      ...tenantInfo,
      supportingCurrencies: selectedCodes,
    });

    // If this is a remove action, delete on server by ID
    if (
      actionMeta.action === 'remove-value' ||
      actionMeta.action === 'pop-value'
    ) {
      const removedCode = actionMeta.removedValue?.value;
      const removedId = supportedCurrencies?.find(
        (c) => c.code === removedCode
      )?.id;
      if (removedId) {
        removeSupportingCurrency(removedId);
      }
    }
  };

  const handleFontChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const font = event.target.value;

    setTenantTheme((prev) => ({
      ...prev,
      styles: {
        ...prev.styles,
        fontFamily: font,
      },
    }));
  };

  const handleAssetUpload = (
    assetType: keyof TenantTheme['assets'],
    url: string
  ) => {
    setTenantTheme((prev) => ({
      ...prev,
      assets: {
        ...prev.assets,
        [assetType]: url,
      },
    }));
  };

  const handleAssetRemove = (assetType: keyof TenantTheme['assets']) => {
    setTenantTheme((prev) => ({
      ...prev,
      assets: {
        ...prev.assets,
        [assetType]: assetType === 'banners' ? [] : '',
      },
    }));
  };

  const handleBannerUpload = (url: string) => {
    setTenantTheme((prev) => {
      if (prev.assets?.banners?.length && prev.assets?.banners?.length >= 10) {
        return prev;
      }

      return {
        ...prev,
        assets: {
          ...prev.assets,
          banners: [...(prev.assets?.banners || []), url],
        },
      };
    });
  };

  const handleBannerRemove = (index: number) => {
    setTenantTheme((prev) => ({
      ...prev,
      assets: {
        ...prev.assets,
        banners: prev.assets?.banners?.filter((_, i) => i !== index),
      },
    }));
  };

  const handleThemeColorChange = (
    colorType: keyof TenantTheme['styles'],
    color: string
  ) => {
    setTenantTheme((prev) => ({
      ...prev,
      styles: {
        ...prev.styles,
        [colorType]: color,
      },
    }));
  };

  useEffect(() => {
    if (tenant) {
      const { name } = tenant;

      // Store initial supporting currencies for API filtering
      const supportedCurrencyCodes =
        supportedCurrencies?.map((currency) => currency.code) || [];
      if (
        initialSupportingCurrenciesRef.current.length === 0 &&
        supportedCurrencyCodes.length > 0
      ) {
        initialSupportingCurrenciesRef.current = supportedCurrencyCodes;
      }

      setTenantInfo({
        name,
        baseCurrency: baseCurrency?.code || tenant.baseCurrency,
        supportingCurrencies: supportedCurrencies?.map(
          (currency) => currency.code
        ),
      });
    }
  }, [tenant, baseCurrency, supportedCurrencies]);

  useEffect(() => {
    const resolved = adminTheme ?? theme;
    if (resolved && screen === 'appearance') {
      setTenantTheme(resolved);
    }
  }, [adminTheme, theme, screen]);

  return {
    tenantInfo,
    tenantTheme,
    BANNER_LIMIT,
    loadingTenant,
    updatingTenant,
    updatingTenantTheme,
    updatingCurrency,
    loadingCurrency,
    loadingSupportedCurrencies,
    loadingBaseCurrency,
    supportedCurrencies,
    baseCurrency,
    updateTenant,
    updateCurrency,
    getCurrencyByCode,
    setTenantTheme,
    updateTenantInfo,
    handleFontChange,
    handleAssetUpload,
    handleAssetRemove,
    updateTenantTheme,
    handleBannerUpload,
    handleBannerRemove,
    handleThemeColorChange,
    updateSupportingCurrencies,
  };
};
